<template>
  <div class="min-h-screen bg-land-canvas text-land-ink">
    <div class="flex min-h-screen">
      <aside
        class="hidden shrink-0 border-r border-land-border bg-land-surface transition-[width] duration-200 lg:flex lg:flex-col"
        :class="navigationCollapsed ? 'w-16' : 'w-64'"
        data-testid="app-sidebar"
      >
        <div class="border-b border-land-border-soft px-3 py-4">
          <div class="flex items-center" :class="navigationCollapsed ? 'justify-center' : 'justify-between gap-2'">
            <a
              href="/"
              class="flex min-w-0 items-center gap-3 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-land-primary"
              :class="navigationCollapsed ? 'justify-center' : ''"
              aria-label="Landport home"
            >
              <div class="grid size-10 shrink-0 place-items-center rounded-md bg-land-primary text-sm font-semibold text-white">
                LP
              </div>
              <div v-if="!navigationCollapsed" class="min-w-0">
                <p class="truncate text-sm font-semibold">Landport</p>
                <p class="truncate text-xs text-land-ink-soft">{{ workspace.environment }} workspace</p>
              </div>
            </a>
            <button
              v-if="!navigationCollapsed"
              type="button"
              class="grid size-9 shrink-0 place-items-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted"
              data-testid="app-sidebar-toggle"
              :aria-label="navigationCollapsed ? 'Expand navigation' : 'Collapse navigation'"
              @click="toggleNavigation"
            >
              <PanelLeftClose class="size-4" aria-hidden="true" />
            </button>
          </div>
          <button
            v-if="navigationCollapsed"
            type="button"
            class="mx-auto mt-3 grid size-9 place-items-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted"
            data-testid="app-sidebar-toggle"
            :aria-label="navigationCollapsed ? 'Expand navigation' : 'Collapse navigation'"
            @click="toggleNavigation"
          >
            <PanelLeftOpen class="size-4" aria-hidden="true" />
          </button>
        </div>

        <nav class="flex-1 space-y-1 px-2 py-4" aria-label="Primary">
          <a
            v-for="item in navigation"
            :key="item.label"
            :href="item.href"
            class="flex h-10 w-full items-center rounded-md text-left text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-land-primary"
            :class="active === item.key ? 'bg-land-primary-soft text-land-primary' : 'text-land-ink-muted hover:bg-land-surface-muted hover:text-land-ink'"
            :title="navigationCollapsed ? item.label : undefined"
            :aria-label="navigationCollapsed ? item.label : undefined"
          >
            <span class="flex size-10 shrink-0 items-center justify-center">
              <component :is="item.icon" class="size-4 shrink-0" aria-hidden="true" />
            </span>
            <span v-if="!navigationCollapsed" class="truncate pr-3">{{ item.label }}</span>
          </a>
        </nav>

        <div v-if="!navigationCollapsed" class="border-t border-land-border-soft p-4">
          <p class="text-xs font-medium uppercase text-land-ink-soft">Workspace</p>
          <p class="mt-1 truncate text-sm font-semibold">{{ workspace.name }}</p>
          <p class="mt-2 text-xs leading-5 text-land-ink-soft">{{ workspace.marketFocus }}</p>
        </div>
      </aside>

      <div class="flex min-w-0 flex-1 flex-col">
        <header class="sticky top-0 z-20 border-b border-land-border bg-white/95 backdrop-blur">
          <div class="flex min-h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <div class="flex min-w-0 flex-1 items-center gap-3 lg:hidden">
              <div class="grid size-9 shrink-0 place-items-center rounded-md bg-land-primary text-xs font-semibold text-white">
                LP
              </div>
              <div class="min-w-0">
                <p class="truncate text-sm font-semibold">Landport</p>
                <p class="truncate text-xs text-land-ink-soft">{{ activeLabel }}</p>
              </div>
            </div>

            <div class="hidden min-w-0 flex-1 lg:block">
              <p class="text-xs font-medium uppercase text-land-ink-soft">{{ activeLabel }}</p>
              <p class="truncate text-sm font-semibold">{{ title }}</p>
            </div>

            <slot name="actions" />
          </div>
        </header>

        <main class="flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div class="mx-auto" :class="maxWidthClass">
            <section class="mb-6 flex flex-col gap-2">
              <p class="flex items-center gap-2 text-sm font-medium text-land-primary">
                <component :is="activeIcon" class="size-4" aria-hidden="true" />
                {{ activeLabel }}
              </p>
              <h1 class="text-2xl font-semibold tracking-normal text-land-ink sm:text-3xl">{{ title }}</h1>
              <p v-if="subtitle" class="max-w-3xl text-sm leading-6 text-land-ink-muted">{{ subtitle }}</p>
            </section>

            <slot />
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Activity, CircleDollarSign, Command, MapPinned, PanelLeftClose, PanelLeftOpen, Star, Zap } from '@lucide/vue'
import { computed, onMounted, ref, watch } from 'vue'

const props = defineProps({
  workspace: {
    type: Object,
    required: true
  },
  active: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  subtitle: {
    type: String,
    default: ''
  },
  maxWidthClass: {
    type: String,
    default: 'max-w-7xl'
  }
})

const navigation = [
  { key: 'command', label: 'Command Center', href: '/', icon: Command },
  { key: 'opportunities', label: 'Opportunities', href: '/opportunities', icon: CircleDollarSign },
  { key: 'favorites', label: 'Favorites', href: '/favorites', icon: Star },
  { key: 'utilities', label: 'Utilities', href: '/utilities', icon: Zap },
  { key: 'sources', label: 'Sources', href: '/sources', icon: Activity },
  { key: 'map', label: 'Map', href: '/map', icon: MapPinned }
]
const NAVIGATION_STORAGE_KEY = 'landport:app-shell:navigation-collapsed'
const navigationCollapsed = ref(false)

const activeItem = computed(() => navigation.find((item) => item.key === props.active) || navigation[0])
const activeLabel = computed(() => activeItem.value.label)
const activeIcon = computed(() => activeItem.value.icon)

onMounted(() => {
  navigationCollapsed.value = globalThis.localStorage?.getItem(NAVIGATION_STORAGE_KEY) === 'true'
})

watch(navigationCollapsed, (collapsed) => {
  globalThis.localStorage?.setItem(NAVIGATION_STORAGE_KEY, String(collapsed))
  dispatchLayoutResize()
})

const toggleNavigation = () => {
  navigationCollapsed.value = !navigationCollapsed.value
}

const dispatchLayoutResize = () => {
  globalThis.dispatchEvent?.(new Event('resize'))
  globalThis.dispatchEvent?.(new CustomEvent('landport:layout-resize'))
  globalThis.setTimeout?.(() => {
    globalThis.dispatchEvent?.(new Event('resize'))
    globalThis.dispatchEvent?.(new CustomEvent('landport:layout-resize'))
  }, 240)
}
</script>
